import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import SendIcon from '@mui/icons-material/Send';

type Props = {
  open: boolean;
  queue: string;
  initialPayload: string;
  isSending: boolean;
  onClose: () => void;
  onSend: (payload: string) => void;
};

function tryPrettify(raw: string): string {
  if (!raw) return '';
  // Le payload stocké peut contenir des guillemets échappés (\") selon la sérialisation
  const candidates = [raw, raw.replace(/\\"/g, '"')];
  for (const candidate of candidates) {
    try {
      return JSON.stringify(JSON.parse(candidate), null, 2);
    } catch {
      // continue
    }
  }
  return raw;
}

function EditMessageDialog({ open, queue, initialPayload, isSending, onClose, onSend }: Props) {
  const [value, setValue] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setValue(tryPrettify(initialPayload));
      setJsonError(null);
    }
  }, [open, initialPayload]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setValue(next);
    try {
      JSON.parse(next);
      setJsonError(null);
    } catch (err: any) {
      setJsonError(err?.message || 'JSON invalide');
    }
  };

  const handleSend = () => {
    onSend(value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Éditer et envoyer le message
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Destination : <strong>{queue || '(queue inconnue)'}</strong>
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Le message d'erreur d'origine restera dans la file d'erreur pour traçabilité.
          Seul le contenu édité ci-dessous sera envoyé sur la queue de destination.
        </Alert>

        <Box
          component="textarea"
          value={value}
          onChange={handleChange}
          spellCheck={false}
          sx={{
            width: '100%',
            minHeight: 400,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            padding: 1,
            boxSizing: 'border-box',
            border: '1px solid',
            borderColor: jsonError ? 'error.main' : 'divider',
            borderRadius: 1,
            resize: 'vertical',
            outline: 'none',
            backgroundColor: '#1a1a1a',
            color: 'whitesmoke',
          }}
        />

        {jsonError && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            JSON invalide : {jsonError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSending}>Annuler</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={isSending || !queue || !value}
        >
          Envoyer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditMessageDialog;
